import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Plus, Search, Filter, LayoutGrid, List, GripVertical,
  Phone, Mail, MapPin, Building2, Calendar, User,
  ChevronRight, MoreVertical, Trash2, Edit2, Upload, Download
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = ["New", "Contacted", "Interested", "Negotiations", "Won", "Lost"];
const STAGE_COLORS = {
  "New": "bg-blue-100 text-blue-700 border-blue-200",
  "Contacted": "bg-purple-100 text-purple-700 border-purple-200",
  "Interested": "bg-amber-100 text-amber-700 border-amber-200",
  "Negotiations": "bg-orange-100 text-orange-700 border-orange-200",
  "Won": "bg-green-100 text-green-700 border-green-200",
  "Lost": "bg-red-100 text-red-700 border-red-200"
};

const SOURCES = ["Website", "Referral", "LinkedIn", "Cold Call", "Event", "Other"];

const SalesCRM = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [bdeUsers, setBdeUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban"); // kanban or list
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [filterBDE, setFilterBDE] = useState("all");
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [leadForm, setLeadForm] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    source: "",
    interest: "",
    location: "",
    expected_value: 0,
    assigned_to: "",
    notes: ""
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/leads/stats`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);

      if (isAdmin) {
        const bdeRes = await axios.get(`${API}/bde-users`);
        setBdeUsers(bdeRes.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.company_name || !leadForm.contact_person) {
      toast.error("Company name and contact person are required");
      return;
    }

    try {
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, leadForm);
        toast.success("Lead updated");
      } else {
        await axios.post(`${API}/leads`, leadForm);
        toast.success("Lead created");
      }
      setShowLeadModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save lead");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { stage: newStage });
      fetchData();
      toast.success(`Moved to ${newStage}`);
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setLeadForm({
      company_name: lead.company_name,
      contact_person: lead.contact_person,
      email: lead.email || "",
      phone: lead.phone || "",
      source: lead.source || "",
      interest: lead.interest || "",
      location: lead.location || "",
      expected_value: lead.expected_value || 0,
      assigned_to: lead.assigned_to || "",
      notes: lead.notes || ""
    });
    setShowLeadModal(true);
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  const resetForm = () => {
    setEditingLead(null);
    setLeadForm({
      company_name: "",
      contact_person: "",
      email: "",
      phone: "",
      source: "",
      interest: "",
      location: "",
      expected_value: 0,
      assigned_to: "",
      notes: ""
    });
  };

  // Handle file import
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validExtensions = ['csv', 'xlsx'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!validExtensions.includes(ext)) {
      toast.error("Please upload a CSV or Excel (.xlsx) file");
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/leads/import-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`${res.data.total_errors} rows had errors`);
      }
      fetchData();
      setShowImportModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterStage !== "all") params.append('stage', filterStage);
      if (filterBDE !== "all") params.append('assigned_to', filterBDE);
      
      const res = await axios.get(`${API}/export/leads?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchQuery || 
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.contact_person.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = filterStage === "all" || lead.stage === filterStage;
    const matchesBDE = filterBDE === "all" || lead.assigned_to === filterBDE;
    return matchesSearch && matchesStage && matchesBDE;
  });

  // Group leads by stage for Kanban
  const leadsByStage = STAGES.reduce((acc, stage) => {
    acc[stage] = filteredLeads.filter(l => l.stage === stage);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-crm-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sales CRM
          </h1>
          <p className="text-slate-500 mt-1">
            {stats?.total_leads || 0} leads • {formatCurrency(stats?.pipeline_value)} pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary"
            data-testid="import-leads-btn"
          >
            <Upload size={16} /> Import
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn-secondary"
            data-testid="export-leads-btn"
          >
            <Download size={16} /> {exporting ? "Exporting..." : "Export"}
          </button>
          <button
            onClick={() => { resetForm(); setShowLeadModal(true); }}
            className="btn-primary"
            data-testid="add-lead-btn"
          >
            <Plus size={18} /> Add Lead
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {STAGES.map(stage => (
          <div key={stage} className={`p-3 rounded-lg border ${STAGE_COLORS[stage]}`}>
            <p className="text-xs font-medium opacity-70">{stage}</p>
            <p className="text-xl font-bold">{stats?.stage_counts?.[stage] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-10"
            data-testid="search-leads"
          />
        </div>
        
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-36 bg-white">
            <Filter size={14} className="mr-2" />
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        {isAdmin && bdeUsers.length > 0 && (
          <Select value={filterBDE} onValueChange={setFilterBDE}>
            <SelectTrigger className="w-40 bg-white">
              <User size={14} className="mr-2" />
              <SelectValue placeholder="BDE" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BDEs</SelectItem>
              {bdeUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-1 ml-auto bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode("kanban")}
            className={`p-2 rounded ${viewMode === "kanban" ? "bg-white shadow-sm" : ""}`}
            data-testid="view-kanban"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded ${viewMode === "list" ? "bg-white shadow-sm" : ""}`}
            data-testid="view-list"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {STAGES.map(stage => (
            <div 
              key={stage} 
              className="flex-shrink-0 w-72 bg-slate-50 rounded-xl p-3"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const leadId = e.dataTransfer.getData("leadId");
                if (leadId) handleStageChange(leadId, stage);
              }}
            >
              <div className={`flex items-center justify-between mb-3 px-2 py-1 rounded-lg ${STAGE_COLORS[stage]}`}>
                <span className="font-semibold text-sm">{stage}</span>
                <span className="text-xs font-bold">{leadsByStage[stage].length}</span>
              </div>
              <div className="space-y-2 min-h-[200px]">
                {leadsByStage[stage].map(lead => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("leadId", lead.id)}
                    onClick={() => handleViewLead(lead)}
                    className="bg-white p-3 rounded-lg border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{lead.company_name}</p>
                        <p className="text-sm text-slate-500 truncate">{lead.contact_person}</p>
                      </div>
                      <GripVertical size={16} className="text-slate-300 cursor-grab" />
                    </div>
                    {lead.interest && (
                      <p className="text-xs text-slate-400 mt-2 truncate">{lead.interest}</p>
                    )}
                    {lead.next_task && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        <Calendar size={12} />
                        {lead.next_task.title}
                      </div>
                    )}
                    {lead.expected_value > 0 && (
                      <p className="text-xs font-semibold text-green-600 mt-2">
                        {formatCurrency(lead.expected_value)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="stat-card p-0" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Interest</th>
                  <th>Location</th>
                  <th>Stage</th>
                  <th>Value</th>
                  <th>Assigned To</th>
                  <th>Next Task</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => (
                  <tr key={lead.id} className="cursor-pointer" onClick={() => handleViewLead(lead)}>
                    <td className="font-medium">{lead.company_name}</td>
                    <td>{lead.contact_person}</td>
                    <td>{lead.phone || "-"}</td>
                    <td className="text-sm text-slate-500">{lead.interest || "-"}</td>
                    <td>{lead.location || "-"}</td>
                    <td>
                      <span className={`badge ${STAGE_COLORS[lead.stage]}`}>{lead.stage}</span>
                    </td>
                    <td className="font-mono">{formatCurrency(lead.expected_value)}</td>
                    <td>{lead.assigned_to_name || "Unassigned"}</td>
                    <td>
                      {lead.next_task ? (
                        <span className="text-xs text-amber-600">{lead.next_task.title}</span>
                      ) : "-"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="btn-ghost p-1"><MoreVertical size={16} /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditLead(lead)}>
                            <Edit2 size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem 
                              className="text-red-600" 
                              onClick={() => handleDeleteLead(lead.id)}
                            >
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLeads.length === 0 && (
            <div className="text-center py-12 text-slate-500">No leads found</div>
          )}
        </div>
      )}

      {/* Add/Edit Lead Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingLead ? "Edit Lead" : "Add New Lead"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={leadForm.company_name}
                  onChange={(e) => setLeadForm({...leadForm, company_name: e.target.value})}
                  placeholder="Acme Coaching"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person *</Label>
                <Input
                  value={leadForm.contact_person}
                  onChange={(e) => setLeadForm({...leadForm, contact_person: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  placeholder="john@acme.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={leadForm.source} onValueChange={(v) => setLeadForm({...leadForm, source: v})}>
                  <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location (City)</Label>
                <Input
                  value={leadForm.location}
                  onChange={(e) => setLeadForm({...leadForm, location: e.target.value})}
                  placeholder="Mumbai"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Interest (Exam/Content)</Label>
                <Input
                  value={leadForm.interest}
                  onChange={(e) => setLeadForm({...leadForm, interest: e.target.value})}
                  placeholder="CLAT, AILET preparation"
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Value (₹)</Label>
                <Input
                  type="number"
                  value={leadForm.expected_value}
                  onChange={(e) => setLeadForm({...leadForm, expected_value: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            {isAdmin && bdeUsers.length > 0 && (
              <div className="space-y-2">
                <Label>Assign to BDE</Label>
                <Select value={leadForm.assigned_to} onValueChange={(v) => setLeadForm({...leadForm, assigned_to: v})}>
                  <SelectTrigger><SelectValue placeholder="Select BDE" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {bdeUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={leadForm.notes}
                onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-[80px]"
                placeholder="Additional notes..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLeadModal(false)}>Cancel</Button>
              <Button type="submit">{editingLead ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Modal */}
      <LeadDetailModal 
        lead={selectedLead} 
        open={showLeadDetail} 
        onClose={() => setShowLeadDetail(false)}
        onUpdate={fetchData}
        bdeUsers={bdeUsers}
        isAdmin={isAdmin}
      />

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-md" data-testid="import-leads-modal">
          <DialogHeader>
            <DialogTitle>Import Leads</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-3">
                Upload a CSV or Excel file with your leads. Required columns:
              </p>
              <ul className="text-sm text-slate-500 list-disc list-inside space-y-1">
                <li><strong>company_name</strong> (required)</li>
                <li><strong>contact_person</strong> (required)</li>
                <li>email, phone, source, interest, location</li>
                <li>expected_value, notes</li>
              </ul>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <Upload size={32} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-3">
                Drop your file here or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileImport}
                className="hidden"
                id="lead-import-file"
              />
              <label htmlFor="lead-import-file">
                <Button asChild variant="outline" disabled={importing}>
                  <span>{importing ? "Importing..." : "Select File"}</span>
                </Button>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Lead Detail Modal Component
const LeadDetailModal = ({ lead, open, onClose, onUpdate, bdeUsers, isAdmin }) => {
  const [leadDetail, setLeadDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    due_date: "",
    task_type: "Follow Up"
  });

  useEffect(() => {
    if (lead && open) {
      fetchLeadDetail();
    }
  }, [lead, open]);

  const fetchLeadDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads/${lead.id}`);
      setLeadDetail(res.data);
    } catch (error) {
      toast.error("Failed to fetch lead details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) {
      toast.error("Task title is required");
      return;
    }
    try {
      await axios.post(`${API}/leads/${lead.id}/tasks`, {
        ...taskForm,
        lead_id: lead.id
      });
      toast.success("Task created");
      setShowTaskForm(false);
      setTaskForm({ title: "", description: "", due_date: "", task_type: "Follow Up" });
      fetchLeadDetail();
      onUpdate();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/lead-tasks/${taskId}?status=Completed`);
      toast.success("Task completed");
      fetchLeadDetail();
      onUpdate();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Building2 size={20} className="text-prepify-600" />
            {lead.company_name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="spinner"></div>
          </div>
        ) : leadDetail && (
          <div className="space-y-6">
            {/* Lead Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-600">
                  <User size={16} />
                  <span>{leadDetail.lead.contact_person}</span>
                </div>
                {leadDetail.lead.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail size={16} />
                    <a href={`mailto:${leadDetail.lead.email}`} className="hover:text-prepify-600">
                      {leadDetail.lead.email}
                    </a>
                  </div>
                )}
                {leadDetail.lead.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone size={16} />
                    <a href={`tel:${leadDetail.lead.phone}`} className="hover:text-prepify-600">
                      {leadDetail.lead.phone}
                    </a>
                  </div>
                )}
                {leadDetail.lead.location && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin size={16} />
                    <span>{leadDetail.lead.location}</span>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-slate-500">Stage:</span>
                  <span className={`ml-2 badge ${STAGE_COLORS[leadDetail.lead.stage]}`}>
                    {leadDetail.lead.stage}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Interest:</span>
                  <span className="ml-2 text-slate-700">{leadDetail.lead.interest || "-"}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Source:</span>
                  <span className="ml-2 text-slate-700">{leadDetail.lead.source || "-"}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Expected Value:</span>
                  <span className="ml-2 font-semibold text-green-600">
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(leadDetail.lead.expected_value || 0)}
                  </span>
                </div>
              </div>
            </div>

            {leadDetail.lead.notes && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{leadDetail.lead.notes}</p>
              </div>
            )}

            {/* Tasks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Tasks</h3>
                <button
                  onClick={() => setShowTaskForm(!showTaskForm)}
                  className="btn-secondary text-sm py-1"
                >
                  <Plus size={14} /> Add Task
                </button>
              </div>

              {showTaskForm && (
                <form onSubmit={handleAddTask} className="p-4 bg-slate-50 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Task title"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                    />
                    <Input
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm">Create Task</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowTaskForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              <div className="space-y-2">
                {leadDetail.tasks.map(task => (
                  <div 
                    key={task.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      task.status === "Completed" ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                    }`}
                  >
                    <div>
                      <p className={`font-medium ${task.status === "Completed" ? "line-through text-slate-400" : ""}`}>
                        {task.title}
                      </p>
                      {task.due_date && (
                        <p className="text-xs text-slate-500">Due: {new Date(task.due_date).toLocaleDateString()}</p>
                      )}
                    </div>
                    {task.status !== "Completed" && (
                      <button
                        onClick={() => handleCompleteTask(task.id)}
                        className="text-sm text-green-600 hover:underline"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                ))}
                {leadDetail.tasks.length === 0 && (
                  <p className="text-center text-slate-400 py-4">No tasks yet</p>
                )}
              </div>
            </div>

            {/* Activity Log */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Activity Log</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {leadDetail.activities.map(activity => (
                  <div key={activity.id} className="flex items-start gap-3 p-2 text-sm">
                    <div className="w-2 h-2 rounded-full bg-prepify-500 mt-1.5"></div>
                    <div>
                      <p className="text-slate-700">
                        <span className="font-medium">{activity.user_name}</span> {activity.action}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                {leadDetail.activities.length === 0 && (
                  <p className="text-center text-slate-400 py-4">No activity yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SalesCRM;
